/**
 * ULID generation on the device.
 *
 * The offline queue needs an id before the server has ever seen the submission,
 * because that id is the idempotency key: the same value goes up on every retry
 * so a lost response cannot turn into a duplicate report.
 *
 * `crypto.randomUUID()` is not usable here — the server validates the field
 * with Laravel's `ulid` rule, which is Crockford base32, not a UUID.
 */

// Crockford's base32: no I, L, O or U, so a handwritten code cannot be misread.
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
const TIME_LENGTH = 10
const RANDOM_LENGTH = 16

function encodeTime(timestamp: number): string {
  let remaining = timestamp
  let output = ''

  for (let i = TIME_LENGTH; i > 0; i--) {
    const modulo = remaining % 32
    output = ENCODING[modulo] + output
    remaining = (remaining - modulo) / 32
  }

  return output
}

function encodeRandom(): string {
  const bytes = new Uint8Array(RANDOM_LENGTH)
  crypto.getRandomValues(bytes)

  let output = ''
  for (let i = 0; i < RANDOM_LENGTH; i++) {
    // 256 is exactly 8 × 32, so the modulo is uniform across the alphabet
    // rather than biased toward its first eight characters.
    output += ENCODING[bytes[i] % 32]
  }

  return output
}

/**
 * A 26-character ULID: 48 bits of millisecond timestamp, then 80 bits of
 * randomness. Lexicographically sortable by creation time, which is why the
 * queue can display items in order without a separate index.
 */
export function ulid(): string {
  return encodeTime(Date.now()) + encodeRandom()
}
